import styled, { css } from 'styled-components';
import { EditorContent } from '@tiptap/react';

import {
  font16,
  FONT_NSW_HANDWRITING,
  linkBase,
  fontSize16LineHeight,
  purpleLinkBase,
  fontWeightMedium,
  fontWeightNormal,
  fontWeightBold,
  fontWeightBolder,
} from 'approot/shared/styles/typography.style';
import {
  grey1,
  grey5,
  grey6,
  grey7,
  grey9,
  grey10,
  purple5,
} from 'approot/shared/styles/colours.style';
import {
  StandardInputProps,
  standardInputStyles,
} from 'approot/shared/styles/inputs.style';
import { rem } from 'approot/shared/style';
import {
  BlockStyle,
  FontFamily,
} from 'approot/shared/richtext/richtext-toolbar.constants';
import { FONT_CLASS_PREFIX } from './richtext-custom-extensions';
import { captionFontSize, captionLineHeight } from './richtext.constants';
import {
  useActivityBuilderFontSize,
  useActivityBuilderFontStyles,
} from 'approot/activity-builder/theme/activity-builder-theme.style';
import { subHeadingFontSize } from './richtext.constants';
import { subHeadingLineHeight } from './richtext.constants';
import { standardFocusState } from 'approot/shared/styles/borders.style';

export type RichtextProps = {
  $isZoomed?: boolean;
  $isSmall?: boolean;
};

export function getFontSize(size: number, isZoomed?: boolean) {
  return isZoomed ? size * 1.5 : size;
}

export const baseSubheadingTextCss = css<RichtextProps>`
  ${props => useActivityBuilderFontStyles({ isZoomed: props.$isZoomed })}

  color: ${grey9};
  font-weight: ${fontWeightMedium};

  strong {
    font-weight: ${fontWeightBolder};
  }
`;

export const baseBodyTextCss = css<RichtextProps>`
  ${props =>
    useActivityBuilderFontStyles({
      isZoomed: props.$isZoomed,
      isSmall: props.$isSmall,
    })}

  color: ${grey9};
  font-weight: ${fontWeightNormal};

  strong {
    font-weight: ${fontWeightBold};
  }
`;

export const baseTextCss = css<RichtextProps>`
  display: block;
  color: ${grey9};
  font-size: ${props => rem(getFontSize(subHeadingFontSize, props.$isZoomed))};
  line-height: ${props =>
    rem(getFontSize(subHeadingLineHeight, props.$isZoomed))};
  font-weight: ${fontWeightNormal};
  strong {
    font-weight: ${fontWeightBold};
  }
`;

export const baseSmallTextCss = css<RichtextProps>`
  display: block;
  color: ${grey7};
  font-size: ${props => rem(getFontSize(captionFontSize, props.$isZoomed))};
  line-height: ${props => rem(getFontSize(captionLineHeight, props.$isZoomed))};
  min-height: ${rem(captionLineHeight)};
  font-weight: ${fontWeightNormal};
`;

export const richtextCommonCss = css<RichtextProps>`
  p,
  h1,
  h2,
  ul,
  ol {
    margin: 0;
  }

  ul,
  ol {
    ${props =>
      props.$isZoomed
        ? `
      padding-left: ${rem(40 * 1.5)};
    `
        : `
       padding-left: ${rem(40)};
    `}
  }

  p {
    ${baseBodyTextCss}
    .${FONT_CLASS_PREFIX}-${FONT_NSW_HANDWRITING} {
    }

    &.small {
      ${props =>
        useActivityBuilderFontStyles({
          isZoomed: props.$isZoomed,
          isSmall: true,
        })}
    }
  }

  h1 {
    ${props => useActivityBuilderFontStyles({ isZoomed: props.$isZoomed })}
    color: ${grey10};
    font-weight: ${fontWeightMedium};

    strong {
      font-weight: ${fontWeightBolder};
    }

    .${FONT_CLASS_PREFIX}-${FONT_NSW_HANDWRITING} {
      font-size: ${props => rem(getFontSize(48, props.$isZoomed))};
      line-height: ${props => rem(getFontSize(48, props.$isZoomed))};
      min-height: ${rem(48)};
    }
  }

  h2 {
    ${baseSubheadingTextCss}

    .${FONT_CLASS_PREFIX}-${FONT_NSW_HANDWRITING} {
      font-size: ${props => rem(getFontSize(40, props.$isZoomed))};
      line-height: ${props => rem(getFontSize(40, props.$isZoomed))};
      min-height: ${rem(40)};
    }
  }

  small {
    ${baseSmallTextCss}

    .${FONT_CLASS_PREFIX}-${FONT_NSW_HANDWRITING} {
      font-size: ${props => rem(getFontSize(30, props.$isZoomed))};
      line-height: ${props => rem(getFontSize(30, props.$isZoomed))};
      min-height: ${rem(30)};
    }
  }

  li.tts-list-item {
    position: relative;
  }

  // this item is hidden to help with the tts list numbering
  li.tts-list-item > p > span.tts-list-number {
    display: none;
  }

  li::marker {
    ${props =>
      useActivityBuilderFontSize({
        isZoomed: props.$isZoomed,
        isSmall: props.$isSmall,
      })}
  }

  a {
    ${purpleLinkBase}
    font-size: inherit;
    line-height: inherit;
  }

  .richtext-readonly {
    user-select: none;
  }
`;

export const richtextInlineCommonCss = css`
  p,
  ul,
  ol {
    margin: 0;
  }

  p {
    ${font16}
    min-height: ${rem(fontSize16LineHeight)};
  }

  a {
    ${linkBase}
  }
`;

type RichtextEditorProps = {
  $fontFamily: FontFamily;
  $blockStyle: BlockStyle;
  $isSmall?: boolean;
};

export const RichtextEditor = styled(EditorContent)<
  RichtextEditorProps & RichtextProps & StandardInputProps
>`
  width: 100%;

  .ProseMirror {
    ${standardInputStyles}
    padding: 0;
    height: auto;
    border: none;
    background-color: ${grey1};
    white-space: normal;

    ${richtextCommonCss}

    &:focus {
      border: none;
    }

    &:hover {
      border: none;
      box-shadow: 0 0 0 1px ${purple5};
    }
  }

  .ProseMirror-focused,
  .ProseMirror-focused:hover {
    ${standardFocusState}
    border: none;
  }

  .ProseMirror .is-empty:first-child {
    overflow: hidden;
  }

  .ProseMirror .is-empty:first-child::before {
    color: ${grey6};
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
    white-space: nowrap;
  }
`;

export const RichtextInlineEditor = styled(EditorContent)<
  StandardInputProps & {
    $minHeight?: number;
    $excludeTools?: boolean;
  }
>`
  ${font16}

  width: 100%;
  flex: 1 1 auto;
  position: relative;

  .ProseMirror {
    ${standardInputStyles}

    min-height: ${({ $minHeight = 160 }) => rem($minHeight)};
    height: auto;
    border: 1px solid ${grey5};
    padding: ${rem(64)} ${rem(16)} ${rem(8)} ${rem(16)};

    ${({ $excludeTools }) =>
      $excludeTools &&
      css`
        padding-top: ${rem(8)};
      `}

    ${richtextInlineCommonCss}
  }

  .ProseMirror-focused {
    border: 1px solid ${purple5};
    outline: 0;
  }

  .ProseMirror .is-empty:first-child::before {
    color: ${grey6};
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
  }
`;
